/**
 * Plataformas — las apps que dialogan con el cliente (Self Social, dashboard
 * de TOM, a medida) y consumen el CM por API.
 *
 * El CM no habla con el cliente final: le pasa la info y los avisos a su
 * plataforma. Acá Champe define, por plataforma, a qué clientes ve, qué puede
 * hacer con cada uno (ver, responder, probar el bot) y qué avisos recibe.
 *
 * Solo rol admin (el backend lo exige en cada endpoint).
 */
import { useState } from "react";
import { Copy, KeyRound, Lock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { relativeTime } from "@/lib/format";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useAdminClients } from "@/hooks/use-admin";
import {
  PLATFORM_EVENTS,
  PLATFORM_PERMISSIONS,
  useCreatePlatform,
  usePlatformDetail,
  usePlatforms,
  useRemovePlatformClient,
  useRotatePlatformToken,
  useRotateWebhookSecret,
  useSetPlatformClientRules,
  useUpdatePlatform,
  type Platform,
  type PlatformClientLink,
  type PlatformSecrets,
} from "@/hooks/use-platforms";

const inputCls =
  "h-8 px-2 text-xs font-sans rounded-md bg-surface-1 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none";
const ghostBtn =
  "inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-xs font-medium transition-colors disabled:opacity-50";
const primaryBtn =
  "h-8 px-3 rounded-lg bg-magenta text-white font-sans text-xs font-medium hover:bg-magenta-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

export function PlatformsPage() {
  const { data: user, isLoading } = useCurrentUser();
  const [secrets, setSecrets] = useState<PlatformSecrets | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <div className="font-sans text-sm text-text-muted">Cargando…</div>
      </div>
    );
  }

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-full bg-background overflow-y-auto">
        <div className="max-w-2xl mx-auto px-7 py-16 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-surface-2 border border-default items-center justify-center mb-4">
            <Lock size={26} strokeWidth={1.5} className="text-text-muted" />
          </div>
          <h1 className="font-sans text-2xl font-semibold text-text-primary">Acceso restringido</h1>
          <p className="font-sans text-sm text-text-muted mt-2">
            Esta sección es solo para administradores.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background overflow-y-auto">
      <div className="max-w-4xl mx-auto px-7 py-8 space-y-6">
        <header>
          <h1 className="font-sans text-3xl font-semibold tracking-tight text-text-primary">Plataformas</h1>
          <p className="font-sans text-sm text-text-muted mt-1">
            Las apps que hablan con el cliente (Self Social, dashboard de TOM…) y consumen el CM.
            El CM nunca le escribe al cliente: le avisa a su plataforma.
          </p>
        </header>

        {secrets && <SecretsBanner secrets={secrets} onClose={() => setSecrets(null)} />}

        <PlatformsList onSecrets={setSecrets} />
      </div>
    </div>
  );
}

function copy(text: string) {
  void navigator.clipboard.writeText(text).then(
    () => toast.success("Copiado"),
    () => toast.error("No se pudo copiar"),
  );
}

function SecretsBanner({ secrets, onClose }: { secrets: PlatformSecrets; onClose: () => void }) {
  return (
    <section className="rounded-lg border border-magenta bg-surface-2 p-4 space-y-3" role="alert">
      <div className="flex items-start gap-2">
        <KeyRound size={16} className="text-magenta mt-0.5 shrink-0" />
        <p className="font-sans text-sm text-text-primary">
          Guardá esto ahora en la plataforma <span className="font-mono">{secrets.slug}</span>. No se vuelve a mostrar.
        </p>
      </div>
      {secrets.token && <SecretLine label="Token (Authorization: Bearer …)" value={secrets.token} />}
      {secrets.webhookSecret && <SecretLine label="Secreto de firma de avisos" value={secrets.webhookSecret} />}
      <button type="button" onClick={onClose} className={ghostBtn}>
        Ya lo guardé
      </button>
    </section>
  );
}

function SecretLine({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-sans text-[11px] text-text-muted mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 break-all rounded-md bg-surface-1 border border-default px-2 py-1.5 font-mono text-xs text-text-primary">
          {value}
        </code>
        <button type="button" onClick={() => copy(value)} className={ghostBtn} aria-label={`Copiar ${label}`}>
          <Copy size={12} />
        </button>
      </div>
    </div>
  );
}

function PlatformsList({ onSecrets }: { onSecrets: (s: PlatformSecrets) => void }) {
  const { data: platforms, isLoading } = usePlatforms();
  const [showForm, setShowForm] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  return (
    <section aria-labelledby="platforms-heading" className="card-surface p-5">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h2 id="platforms-heading" className="font-sans text-md font-semibold text-text-primary">
          Plataformas conectadas
        </h2>
        {!showForm && (
          <button type="button" onClick={() => setShowForm(true)} className={ghostBtn}>
            <Plus size={12} strokeWidth={2} />
            Conectar plataforma
          </button>
        )}
      </div>

      {showForm && (
        <CreatePlatformForm
          onClose={() => setShowForm(false)}
          onCreated={(s) => {
            setShowForm(false);
            onSecrets(s);
            setOpen(s.slug);
          }}
        />
      )}

      {isLoading ? (
        <p className="font-sans text-xs text-text-muted">Cargando plataformas…</p>
      ) : !platforms?.length ? (
        <p className="font-sans text-xs text-text-muted">Todavía no hay plataformas conectadas.</p>
      ) : (
        <ul className="space-y-2">
          {platforms.map((p) => (
            <li key={p.slug} className="rounded-lg border border-default">
              <button
                type="button"
                onClick={() => setOpen(open === p.slug ? null : p.slug)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-surface-2 rounded-lg"
                aria-expanded={open === p.slug}
              >
                <div className="min-w-0">
                  <div className="font-sans text-sm font-medium text-text-primary">{p.name}</div>
                  <div className="font-mono text-[11px] text-text-muted truncate">
                    {p.slug} · {p.tokenPrefix}… · {p.lastUsedAt ? `usada ${relativeTime(p.lastUsedAt)}` : "sin uso todavía"}
                  </div>
                </div>
                <span
                  className={cn(
                    "shrink-0 font-sans text-[11px] px-2 py-0.5 rounded-full border",
                    p.active ? "border-default text-text-secondary" : "border-default text-text-muted line-through",
                  )}
                >
                  {p.active ? "activa" : "desactivada"}
                </span>
              </button>
              {open === p.slug && <PlatformPanel platform={p} onSecrets={onSecrets} />}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CreatePlatformForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (s: PlatformSecrets) => void;
}) {
  const create = useCreatePlatform();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [canCreateClients, setCanCreateClients] = useState(false);

  function handleSubmit() {
    if (!slug.trim() || !name.trim()) return;
    create.mutate(
      { slug: slug.trim(), name: name.trim(), webhookUrl: webhookUrl.trim() || null, canCreateClients },
      { onSuccess: (res) => onCreated({ slug: res.platform.slug, token: res.token, webhookSecret: res.webhookSecret }) },
    );
  }

  return (
    <div className="rounded-lg border border-default bg-surface-2 p-3 mb-3 space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value.toLowerCase())}
          placeholder="slug (ej: self-social, tom-dashboard)"
          className={cn(inputCls, "font-mono")}
        />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre" className={inputCls} />
        <input
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          placeholder="Dirección de avisos (https://…) — opcional"
          className={cn(inputCls, "sm:col-span-2 font-mono")}
        />
      </div>
      <label className="flex items-center gap-2 font-sans text-xs text-text-secondary">
        <input type="checkbox" checked={canCreateClients} onChange={(e) => setCanCreateClients(e.target.checked)} />
        Puede dar de alta clientes (Self Social sí; un dashboard a medida, no)
      </label>
      <div className="flex items-center gap-2">
        <button type="button" onClick={handleSubmit} disabled={!slug.trim() || !name.trim() || create.isPending} className={primaryBtn}>
          {create.isPending ? "Creando…" : "Crear y generar token"}
        </button>
        <button type="button" onClick={onClose} className="h-8 px-3 rounded-lg text-xs font-sans text-text-muted hover:text-text-primary">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function PlatformPanel({ platform, onSecrets }: { platform: Platform; onSecrets: (s: PlatformSecrets) => void }) {
  const { data, isLoading } = usePlatformDetail(platform.slug);
  const update = useUpdatePlatform();
  const rotateToken = useRotatePlatformToken();
  const rotateSecret = useRotateWebhookSecret();
  const [webhookUrl, setWebhookUrl] = useState(platform.webhookUrl ?? "");

  function confirmRotateToken() {
    if (!window.confirm(`El token actual de ${platform.name} deja de andar en el acto. ¿Generar uno nuevo?`)) return;
    rotateToken.mutate(platform.slug, { onSuccess: (r) => onSecrets({ slug: platform.slug, token: r.token }) });
  }

  function confirmRotateSecret() {
    if (!window.confirm(`Los avisos a ${platform.name} se van a firmar con un secreto nuevo. ¿Seguir?`)) return;
    rotateSecret.mutate(platform.slug, { onSuccess: (r) => onSecrets({ slug: platform.slug, webhookSecret: r.webhookSecret }) });
  }

  return (
    <div className="border-t border-default px-3 py-3 space-y-4">
      <div className="space-y-2">
        <div className="font-sans text-xs font-medium text-text-secondary">Dirección de avisos</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://…"
            className={cn(inputCls, "flex-1 min-w-[200px] font-mono")}
          />
          <button
            type="button"
            className={ghostBtn}
            disabled={update.isPending || webhookUrl.trim() === (platform.webhookUrl ?? "")}
            onClick={() => update.mutate({ slug: platform.slug, webhookUrl: webhookUrl.trim() || null })}
          >
            Guardar
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" className={ghostBtn} onClick={confirmRotateToken} disabled={rotateToken.isPending}>
          <KeyRound size={12} /> Rotar token
        </button>
        <button type="button" className={ghostBtn} onClick={confirmRotateSecret} disabled={rotateSecret.isPending}>
          <KeyRound size={12} /> Rotar secreto de avisos
        </button>
        <label className="inline-flex items-center gap-2 font-sans text-xs text-text-secondary">
          <input
            type="checkbox"
            checked={platform.canCreateClients}
            onChange={(e) => update.mutate({ slug: platform.slug, canCreateClients: e.target.checked })}
          />
          Da de alta clientes
        </label>
        <button
          type="button"
          className={ghostBtn}
          onClick={() => {
            if (platform.active && !window.confirm(`${platform.name} va a dejar de poder usar el CM. ¿Desactivar?`)) return;
            update.mutate({ slug: platform.slug, active: !platform.active });
          }}
        >
          {platform.active ? "Desactivar" : "Reactivar"}
        </button>
      </div>

      {isLoading || !data ? (
        <p className="font-sans text-xs text-text-muted">Cargando…</p>
      ) : (
        <>
          <ClientRules platformSlug={platform.slug} links={data.clients} />
          <RecentEvents events={data.events} />
        </>
      )}
    </div>
  );
}

function ClientRules({ platformSlug, links }: { platformSlug: string; links: PlatformClientLink[] }) {
  const { data: clients } = useAdminClients();
  const setRules = useSetPlatformClientRules();
  const remove = useRemovePlatformClient();
  const [adding, setAdding] = useState("");
  const linked = new Set(links.map((l) => l.clientSlug));
  const available = (clients ?? []).filter((c) => !linked.has(c.slug));

  function toggle(link: PlatformClientLink, field: "permissions" | "events", value: string) {
    const current = new Set(link[field]);
    if (current.has(value)) current.delete(value);
    else current.add(value);
    setRules.mutate({
      slug: platformSlug,
      clientSlug: link.clientSlug,
      permissions: field === "permissions" ? [...current] : link.permissions,
      events: field === "events" ? [...current] : link.events,
    });
  }

  return (
    <div className="space-y-2">
      <div className="font-sans text-xs font-medium text-text-secondary">Clientes y reglas</div>
      {links.length === 0 ? (
        <p className="font-sans text-xs text-text-muted">Esta plataforma todavía no ve a ningún cliente.</p>
      ) : (
        <ul className="space-y-1.5">
          {links.map((l) => (
            <li key={l.clientSlug} className="rounded-md border border-default px-3 py-2 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-text-primary">
                  {l.clientSlug}
                  {l.createdByPlatform && <span className="font-sans text-text-muted"> · alta de la plataforma</span>}
                </span>
                <button
                  type="button"
                  aria-label={`Quitar ${l.clientSlug}`}
                  className="text-text-muted hover:text-text-primary"
                  onClick={() => {
                    if (window.confirm(`La plataforma deja de ver a ${l.clientSlug}. ¿Seguir?`)) {
                      remove.mutate({ slug: platformSlug, clientSlug: l.clientSlug });
                    }
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {PLATFORM_PERMISSIONS.map((perm) => (
                  <label key={perm.value} className="inline-flex items-center gap-1.5 font-sans text-xs text-text-secondary" title={perm.description}>
                    <input type="checkbox" checked={l.permissions.includes(perm.value)} onChange={() => toggle(l, "permissions", perm.value)} />
                    {perm.label}
                  </label>
                ))}
                <span className="text-text-muted text-xs">|</span>
                {PLATFORM_EVENTS.map((ev) => (
                  <label key={ev.value} className="inline-flex items-center gap-1.5 font-sans text-xs text-text-secondary">
                    <input type="checkbox" checked={l.events.includes(ev.value)} onChange={() => toggle(l, "events", ev.value)} />
                    Aviso: {ev.label}
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
      {available.length > 0 && (
        <div className="flex items-center gap-2">
          <select value={adding} onChange={(e) => setAdding(e.target.value)} className={inputCls}>
            <option value="">Sumar un cliente…</option>
            {available.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name} ({c.slug})
              </option>
            ))}
          </select>
          <button
            type="button"
            className={ghostBtn}
            disabled={!adding || setRules.isPending}
            onClick={() =>
              setRules.mutate(
                { slug: platformSlug, clientSlug: adding, permissions: ["read"], events: ["escalation"] },
                { onSuccess: () => setAdding("") },
              )
            }
          >
            <Plus size={12} /> Sumar
          </button>
        </div>
      )}
    </div>
  );
}

const EVENT_STATUS_LABEL: Record<string, string> = {
  pending: "pendiente",
  delivered: "entregado",
  failed: "falló",
};

function RecentEvents({ events }: { events: { id: string; clientSlug: string; type: string; status: string; attempts: number; lastError: string | null; createdAt: string }[] }) {
  if (events.length === 0) {
    return <p className="font-sans text-xs text-text-muted">Sin avisos enviados todavía.</p>;
  }
  return (
    <div className="space-y-1.5">
      <div className="font-sans text-xs font-medium text-text-secondary">Últimos avisos</div>
      <ul className="space-y-1">
        {events.map((e) => (
          <li key={e.id} className="flex items-center justify-between gap-2 font-sans text-xs">
            <span className="text-text-secondary truncate">
              {e.type} · <span className="font-mono">{e.clientSlug}</span> · {relativeTime(e.createdAt)}
            </span>
            <span
              className={cn("shrink-0", e.status === "failed" ? "text-red-500" : "text-text-muted")}
              title={e.lastError ?? undefined}
            >
              {EVENT_STATUS_LABEL[e.status] ?? e.status}
              {e.attempts > 1 ? ` (${e.attempts} intentos)` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
